import {PackageTypes, PackageTypes_Reverse, getDirectionInTier2} from "../dataStore/FilterDataStore"
import {getFeatureStatus, getNodeCapabilityList, oldTimeZoneMapper} from "../CommonUtils";
import {FEATURES} from "../constants/CommonConstants";
import {getCalendarDayStartEpoch, getCalendarDayEndEpoch, getBrowserTimeZone} from "../utils/stationTime";

// Views that receive a default calendar-day range filter when no explicit date is selected.
// Add new view keys here instead of adding if-branches.
const DEFAULT_CALENDAR_DAY_RANGE_VIEWS = { RTS_VIEW: true };

/**
 * Builds the EAD range ({startTime, endTime} in epoch seconds) for the RTS
 * calendar-day default: from the current station-local calendar-day start to now.
 *
 * Timezone source order: the filter's stationTimeZone (when threaded through) ->
 * the browser runtime tz (station-local machine; available on first paint) -> UTC.
 * Legacy aliases (US/Pacific, IST, ...) are normalized to IANA ids; the underlying
 * helper never throws and degrades to UTC midnight on any failure.
 *
 * Shared by the RTS metric tile (this converter) and the RTS drilldown so the
 * click-through list is bounded to the same window as the count it came from.
 *
 * @param {object} filter - initialized filter (may carry stationTimeZone)
 * @param {Date} [now] - current instant (injectable for tests)
 * @returns {{startTime:number, endTime:number}}
 */
export function resolveCalendarDayEadRange(filter, now = new Date()) {
    const resolvedTimeZone = oldTimeZoneMapper((filter && filter.stationTimeZone) || getBrowserTimeZone());
    return {
        // "current calendar day (00:00–23:59 local station time)".
        // startTime = 00:00 station-local (getCalendarDayStartEpoch),
        // endTime   = 23:59:59 station-local (getCalendarDayEndEpoch).
        // Previous impl used "now" for endTime, which would miss packages
        // with EAD later today (if EAD is stored as a specific timestamp).
        "startTime" : getCalendarDayStartEpoch(now, resolvedTimeZone),
        "endTime" : getCalendarDayEndEpoch(now, resolvedTimeZone)
    };
}

/**
 * True when no explicit date filter is selected, i.e. the calendar-day default
 * should supply the EAD range.
 */
export function hasNoExplicitDateFilter(filter) {
    return !!filter && !filter.startPDD && !filter.endPDD && !filter.shipDateStart && !filter.shipDateEnd;
}

export const FilterConverter = async (filter, view, leg, statusToDirectionMap, state, packageStatus, isCPTFilterEnabled) => {

    var filterMap = {};
    var rangeFilterMap = {};

    if (view !== "AGEING_VIEW" && view !== "RTS_VIEW" && view !== "DWELLING_VIEW" && view !==  "SD_DWELLING_VIEW"
            && filter.lastUpdatedStart === "" && filter.lastUpdatedEnd === "") {

        filterMap["DWELL_TIME"] = [(filter.dwellEnd !== "" ? filter.dwellEnd : 0) + "_" + filter.dwellEndTimeUnit.toUpperCase(),  
        (filter.dwellStart !== "" ? filter.dwellStart : 1440) + "_" + filter.dwellStartTimeUnit.toUpperCase()]
    }

    if (!isCPTFilterEnabled && filter.cycles.length > 0 ) {

        filterMap["CYCLE"] = filter.cycles.map(cycle => { return cycle});

    }

    if (isCPTFilterEnabled && filter.CPT.length > 0 ) {

        filterMap["DISPATCH_BY"] = filter.CPT.map(CPTDetail => {return CPTDetail});

    }

    if (filter.deliveryWindow.length > 0 ) {

        filterMap["DELIVERY_WINDOW"] = filter.deliveryWindow.map(deliveryWindow => { return deliveryWindow});

    }

    const isDepartFilteringEnabled = await getFeatureStatus(FEATURES.DEPART_FILTERING_ENABLED)
    if(isDepartFilteringEnabled) {
        if ((view === "AGEING_VIEW" || view === "RTS_VIEW") && leg === "REVERSE") {
            const nodeCapability = await getNodeCapabilityList();
            if (!nodeCapability || nodeCapability ==="[]" || nodeCapability.includes("AMZL_CORE")) {
                filterMap["SOURCE_NODE"] = ["CHILD"];
                filterMap["DESTINATION_NODE"] = ["SELF"];
            } else if (nodeCapability.includes("HANDOVER")) {
                filterMap["SOURCE_NODE"] = ["SELF"];
                filterMap["DESTINATION_NODE"] = ["PARENT"];
            }
        }
    }

    var statusList = state ? [state] : packageStatus;

    //Map of status and list of directions
    if (statusList.length > 0 ) {
            
        statusList.forEach( status => {
                let packageState = status.split(" (", 1)[0];
    
                //Case 1 : Direction in tier 1
                if(status.split(" (", 2)[1] !== undefined) {

                    statusToDirectionMap[packageState] = statusToDirectionMap[packageState] ? statusToDirectionMap[packageState].concat([status.split(" (", 2)[1].split(")", 1)[0]]): [status.split(" (", 2)[1].split(")", 1)[0]]; 
                } else {

                   var direction = getDirectionInTier2(leg, status)
                   statusToDirectionMap[packageState] = direction.split(" (", 2)[1] !== undefined ? 
                        [direction.split(" (", 2)[1].split(")", 1)[0]]: [];
                }
            });
        }

    //Check for both filters
    if (filter.startPDD || filter.endPDD) {

        rangeFilterMap["EAD"] = {
            "startTime" : filter.startPDD ? new Date(filter.startPDD).getTime() / 1000 : new Date(filter.endPDD).getTime() / 1000,
            //Passing same PDD in case end pdd is not selected
            "endTime" : filter.endPDD ? new Date(filter.endPDD).getTime() / 1000 : new Date(filter.startPDD).getTime() / 1000
        }
    }
    //Check for both filters
    if (filter.shipDateStart || filter.shipDateEnd) {

        rangeFilterMap["SHIP_DATE"] = {
            "startTime" : filter.shipDateStart ? new Date(filter.shipDateStart).getTime() / 1000 : new Date(filter.shipDateEnd).getTime() / 1000,
            "endTime" : filter.shipDateEnd ? new Date(filter.shipDateEnd).getTime() / 1000 : new Date(filter.shipDateStart).getTime() / 1000
        }
    }

    // Default calendar-day range: when a configured view (RTS) loads with no explicit
    // date filter, bound results to the current station-local calendar day so stale
    // prior-day packages are excluded. Gated by a feature flag for per-station rollout.
    if (DEFAULT_CALENDAR_DAY_RANGE_VIEWS[view] && hasNoExplicitDateFilter(filter)) {
        const isCalendarDayDefaultEnabled = await getFeatureStatus(FEATURES.IS_RTS_CALENDAR_DAY_DEFAULT_ENABLED);
        if (isCalendarDayDefaultEnabled) {
            rangeFilterMap["EAD"] = resolveCalendarDayEadRange(filter);
        }
    }

    if (filter.sameDay) {

        filterMap["SAME_DAY"] = [filter.sameDay.toString()]
    }

    if (leg) {
        
        if (leg === "FORWARD") {

            filterMap["SHIPMENT_TYPE"] = ["Delivery"]
         } 
    }

    if (filter.packageTypes.length > 0) {

        //Adding for dwelling
        filterMap["SHIPMENT_TYPE"] = [].concat.apply([], filter.packageTypes.map(packageType => {return PackageTypes[packageType]}));

    }

    if (filter.packageTypes_Reverse.length > 0) {

        //Adding for reverse leg in Ageing
         var displayShipType = [].concat.apply([], filter.packageTypes_Reverse.map(packageType => {return PackageTypes_Reverse[packageType]}));
         filterMap["SHIPMENT_TYPE"] = [].concat.apply([], displayShipType.map(shipType => {return PackageTypes[shipType]}))

    }

    if (filter.stationArrivalTime.length > 0) {

        filterMap["LOCAL_STATION_ARRIVAL_TIME"] = filter.stationArrivalTime;
    }

    if (filter.eadSection) {

        filterMap["EAD_SECTION"] = ["true"];
    }

    if (filter.shipOption) {
        filterMap["SHIPMENT_OPTION"] = filter.shipOption;
    }

    return [{
        "__type" : "TermFilter:http://internal.amazon.com/coral/com.amazon.oculusservice.model.filter/"
        ,"filterMap": filterMap},{

        "__type" : "RangeFilter:http://internal.amazon.com/coral/com.amazon.oculusservice.model.filter/"
        ,"filterMap": rangeFilterMap

    }]
}


// WEBPACK FOOTER //
// ./src/commons/filter/FilterConverter.js